import { Input } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';

const THF_CODE_EDITOR_THEMES = ['vs-dark', 'vs', 'hc-black'];
const THF_CODE_EDITOR_THEME_DEFAULT = 'vs';

/**
 * @description
 *
 * O `thf-code-editor` Ã© um componente para ediÃ§Ã£o de cÃ³digo fonte baseado no Monaco Editor da Microsoft.
 *
 * Sendo assim, algumas configuraÃ§Ãµes presentes no Monaco podem ser utilizadas aqui, como a escolha da liguagem
 * (utilizando o highlight syntax especÃ­fico), escolha do tema e opÃ§Ã£o de diff, alÃ©m de ser muito similar ao Visual
 * Studio Code, com autocomplete e fechamento automÃ¡tico de brackets.
 *
 * Este componente pode ser usado em qualquer situaÃ§Ã£o que necessite de adiÃ§Ã£o de cÃ³digos, como por exemplo, criar
 * receitas utilizando Terraform para gerenciar topologias.
 * Ã importante ressaltar que este nÃ£o Ã© um componente para ediÃ§Ã£o de textos comuns.
 *
 * O [(ngModel)] deve ser usado para manipular o conteÃºdo do thf-code-editor, ou seja, tanto para incluir um conteÃºdo quanto
 * para recuperar o conteÃºdo do thf-code-editor, utiliza-se uma variÃ¡vel passada por [(ngModel)].
 *
 * > NÃ£o esqueÃ§a de fazer a instalaÃ§Ã£o do pacote `@totvs/thf-code-editor` em sua aplicaÃ§Ã£o.
 * >
 * > ```
 * > npm i --save @totvs/thf-code-editor
 * > ```
 * >
 * > Adicionar o mÃ³dulo `ThfCodeEditorModule` em seu projeto:
 * >
 * > ```
 * > // app.module.ts
 * > ...
 * > import { ThfModule } from '@totvs/thf-ui';
 * > import { ThfCodeEditorModule } from '@totvs/thf-code-editor';
 * > ...
 * > @NgModule({
 * >   imports: [
 * >     ...
 * >     ThfModule,
 * >     ThfCodeEditorModule
 * >   ],
 * >   ...
 * > })
 * > export class AppModule { }
 * > ```
 * >
 * > E adicionar um *asset* ao arquivo `angular.json`, conforme exemplo abaixo:
 * >
 * > <pre ngNonBindable>
 * > "assets": [
 * >    { "glob": "&#8727;&#8727;/&#8727;", "input": "node_modules/monaco-editor/min", "output": "/assets/monaco/" }
 * >  ]
 * > </pre>
 *
 */
export abstract class ThfCodeEditorBaseComponent implements ControlValueAccessor {

  private _height: number = 150;
  private _language = 'plainText';
  private _readonly: boolean = false;
  private _showDiff: boolean = false;
  private _theme = THF_CODE_EDITOR_THEME_DEFAULT;

  editor: any;
  modifiedValue: string = '';
  value: any = '';

  /**
   * @optional
   *
   * @description
   *
   * Linguagem na qual serÃ¡ apresentado o cÃ³digo fonte.
   * Para saber quais sÃ£o as linguagens compatÃ­veis, consulte a documentaÃ§Ã£o oficial do
   * [**Monaco Editor**](https://microsoft.github.io/monaco-editor/).
   *
   * TambÃ©m Ã© possÃ­vel adicionar uma nova linguagem personalizada utilizando o serviÃ§o:
   * [**thf-code-editor-register**](https://thf.totvs.com.br/documentation/thf-code-editor-register?view=doc).
   *
   * @default `plainText`
   */
  @Input('t-language') set language(language: string) {
    this._language = language && language.length ? language.trim() : 'plainText';

    if (this.editor && this._language) {
      this.setLanguage(this._language);
    }
  }

  get language(): string {
    return this._language;
  }

  /**
   * @optional
   *
   * @description
   *
   * Indica se o editor serÃ¡ aberto em modo de leitura.
   *
   * Neste caso, nÃ£o Ã© possÃ­vel editar o cÃ³digo inserido.
   *
   * Obs: Esta propriedade nÃ£o refletirÃ¡ efeito se alterada apÃ³s o carregamento do componente.
   *
   * @default `false`
   */
  @Input('t-readonly') set readonly(readonly: boolean) {
    this._readonly = <any>readonly === '' ? true : this.convertToBoolean(readonly);

    if (this.editor) {
      this.setReadOnly(readonly);
    }

  }

  get readonly(): boolean {
    return this._readonly;
  }

  /**
   * @optional
   *
   * @description
   *
   * Indica se o editor serÃ¡ aberto em modo de comparaÃ§Ã£o.
   *
   * Caso esteja habilitada esta opÃ§Ã£o, entÃ£o o [(ngModel)] deverÃ¡ ser passado como um array, cuja primeira opÃ§Ã£o deve
   * conter uma string com o cÃ³digo original e na segunda posiÃ§Ã£o uma string cÃ³digo modificado para efeito de
   * comparaÃ§Ã£o. Neste caso, o usuÃ¡rio conseguirÃ¡ editar apenas o cÃ³digo modificado e isso refletirÃ¡ na segunda posiÃ§Ã£o
   * do array consequentemente.
   *
   * Obs: Esta propriedade nÃ£o refletirÃ¡ efeito se alterada apÃ³s o carregamento do componente.
   *
   * @default `false`
   */
  @Input('t-show-diff') set showDiff(showDiff: boolean) {
    this._showDiff = <any>showDiff === '' ? true : this.convertToBoolean(showDiff);
  }

  get showDiff(): boolean {
    return this._showDiff;
  }

  /**
   * @optional
   *
   * @description
   *
   * Define um tema para o editor.
   *
   * Temas vÃ¡lidos:
   *  - `vs-dark`
   *  - `vs`
   *  - `hc-black`
   *
   * Ã importante salientar que o tema serÃ¡ aplicados a todos os componentes thf-code-editor existentes na tela,
   * ou seja, todas as instÃ¢ncias do componente receberÃ£o o Ãºltimo tema atribuÃ­do ou o tema da Ãºltima instÃ¢ncia
   * criada.
   *
   * @default `vs`
   */
  @Input('t-theme') set theme(theme: string) {
    this._theme = THF_CODE_EDITOR_THEMES.includes(theme) ? theme : THF_CODE_EDITOR_THEME_DEFAULT;

    if (this.editor) {
      this.setTheme(theme);
    }
  }

  get theme(): string {
    return this._theme;
  }

  /**
   * @optional
   *
   * @description
   *
   * Define a altura do componente em pixels do thf-code-editor.
   * Esta propriedade nÃ£o poderÃ¡ ser alterada apÃ³s o componente ter sido iniciado.
   * A altura mÃ­nima Ã© 150 pixels.
   */
  @Input('t-height') set height(height: string) {
    this._height = parseFloat(height) >= 150 ? parseFloat(height) : 150;
  }

  get height(): string {
    return `${this._height}px`;
  }

  onTouched = (value: any) => {};
  onChangePropagate = (value: any) => {};

  getOptions() {
    return {language: this.language, theme: this.theme, readOnly: this.readonly};
  }

  registerOnChange(fn: any): void {
    this.onChangePropagate = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  abstract writeValue(value: any);

  abstract setLanguage(value: any);

  abstract setTheme(value: any);

  abstract setReadOnly(value: any);

  protected convertToBoolean(val: any): boolean {
    if (typeof val === 'string') {
      val = val.toLowerCase().trim();
      return (val === 'true' || val === 'on' || val === '');
    }

    if (typeof val === 'number') {
      return val === 1;
    }

    return !!val;
  }

}
